//App
import {
    EmployeePledgeGetDto,
    PledgeGiftInvestmentSaveDto,
    PledgeSaveDto,
} from "../app/model/api/types/dto";
import { CreditCardInformationDto } from "../app/model/api/types/dto/creditCardInformationDto";
import { PaymentOption } from "../types";
import { EmployeePledgeGiftInvestmentViewModel } from "./employeePledgeGiftInvestmentViewModel";

export class EmployeePledgeViewModel {
    giftInvestmentAmount: EmployeePledgeGiftInvestmentViewModel[] | null = [];
    constructor(
        public id: string | null = null,
        public campaignId: string | null = null,
        public isNotWillingToContribute: boolean | null = false,
        public contributionOptionId: string | null = null,
        public contributionAmount: number | null = null,
        public paymentOptionId: string | null = null,
        public isWillingToStayAnonymous: string | null = null,
        public isWillingToBeIncludedInCompanyEvents: string | null = null,
        public isWillingToCombineSpouseContribution: string | null = null,
        public isWillingToChooseAgenciesForContribution: string | null = null,
        public giftInvestment: EmployeePledgeGiftInvestmentViewModel[] | null = [],
        public otherAnnualPledgeAmount: number | null = null,
        public creditCardNumber: number | null = null,
        public cardHolderFirstName: string | null = null,
        public cardHolderLastName: string | null = null,
        public expirationDate: string | null = null,
        public cvv: string | null = null,
        public zip: string | null = null,
        public spouseName: string | null = null,
        public spouseWorkPlace: string | null = null,
        public spousePledgeAmount: number | null = null,
        public payrollDeductionFrequency: string | null = null,
        public organizationContactName: string | null = null,
        public organizationContactEmail: string | null = null,
        public submitDate: Date | null = null,
        public creditCardDeductionFrequencyId: string | null = null,
        public installment: number | null = null,
        public interval: number | null = null,
        public isRecurringContribution: boolean = false,
        public stepUpEnrollment: boolean | null = false,
        public leadershipEnrollment: boolean | null = false,
        public contributorId: string | null = null,
        public stepUpMinDonationAmount: number | null = null,
        public leadershipMinDonationAmount: number | null = null,
        public organizationContactPhone: string | null = null,
        public subscriptionId: string | null = null,
        public campaignNotificationSentDate: Date | null = null
    ) { }

    private parseBooleanString(value: string | null): boolean | null {
        if (value === "1") return true;
        if (value === "2") return false;
        return null;
    }

    private toCreditCardInformationDto(): CreditCardInformationDto | null {
        if (this.paymentOptionId !== PaymentOption.CreditCard) return null;

        return {
            creditCardNumber: this.creditCardNumber?.toString() ?? null,
            cardHolderFirstName: this.cardHolderFirstName,
            cardHolderLastName: this.cardHolderLastName,
            expirationDate: this.expirationDate,
            cardSecurityCode: this.cvv,
            zipCode: this.zip,
        };
    }

    private toPledgeGiftInvestmentSaveDto(): PledgeGiftInvestmentSaveDto[] {
        if (!this.giftInvestment) return [];

        return this.giftInvestment.flatMap((value) => ({
            id: value.id,
            pledgeId: this.id,
            giftInvestmentId: value.giftInvestmentId ?? null,
            agencyId: value.agencyId ?? null,
            pledgeAmount: value.pledgeAmount ?? null,
            otherAgencyName: value.otherAgencyName,
        }));
    }

    toDto(): PledgeSaveDto {
        return {
            id: this.id,
            contributorId: this.contributorId,
            isWillingToContribute: !this.isNotWillingToContribute,
            isWillingToStayAnonymous: this.parseBooleanString(this.isWillingToStayAnonymous),
            isWillingToBeIncludedInCompanyEvents: this.parseBooleanString(this.isWillingToBeIncludedInCompanyEvents),
            isWillingToCombineSpouseContribution: this.parseBooleanString(this.isWillingToCombineSpouseContribution),
            isWillingtoChooseAgenciesForContribution: this.parseBooleanString(this.isWillingToChooseAgenciesForContribution),
            campaignId: this.campaignId ?? null,
            contributionOptionId: this.contributionOptionId ?? null,
            contributionAmount: this.contributionAmount,
            paymentOptionId: this.paymentOptionId ?? null,
            giftInvestments: this.toPledgeGiftInvestmentSaveDto(),
            creditCardInformation: this.toCreditCardInformationDto(),
            spouseName: this.spouseName,
            spouseWorkPlace: this.spouseWorkPlace,
            spousePledgeAmount: this.spousePledgeAmount,
            isActive: true,
            isDeleted: false,
            hasResponded: true,
            campaignNotificationSentDate: this.campaignNotificationSentDate,
            payrollDeductionFrequency: this.payrollDeductionFrequency,
            organizationContactEmail: this.organizationContactEmail,
            organizationContactName: this.organizationContactName,
            organizationContactPhone: this.organizationContactPhone,
            submitDate: this.submitDate,
            creditCardDeductionFrequencyId: this.creditCardDeductionFrequencyId,
            installment: this.installment,
            interval: this.interval,
            isRecurringContribution: this.isRecurringContribution,
            stepUpEnrollment: this.stepUpEnrollment,
            stepUpMinDonationAmount: this.stepUpMinDonationAmount,
            leadershipEnrollment: this.leadershipEnrollment,
            leadershipMinDonationAmount: this.leadershipMinDonationAmount,
            subscriptionId: this.subscriptionId,
        };
    }
}

export const initializeEmployeePledgeViewModel = (
    employeePledgeDetails: EmployeePledgeViewModel
): EmployeePledgeViewModel => {
    return new EmployeePledgeViewModel(
        employeePledgeDetails.id,
        employeePledgeDetails.campaignId,
        employeePledgeDetails.isNotWillingToContribute,
        employeePledgeDetails.contributionOptionId,
        employeePledgeDetails.contributionAmount,
        employeePledgeDetails.paymentOptionId,
        employeePledgeDetails.isWillingToStayAnonymous,
        employeePledgeDetails.isWillingToBeIncludedInCompanyEvents,
        employeePledgeDetails.isWillingToCombineSpouseContribution,
        employeePledgeDetails.isWillingToChooseAgenciesForContribution,
        employeePledgeDetails.giftInvestment,
        employeePledgeDetails.otherAnnualPledgeAmount,
        employeePledgeDetails.creditCardNumber,
        employeePledgeDetails.cardHolderFirstName,
        employeePledgeDetails.cardHolderLastName,
        employeePledgeDetails.expirationDate,
        employeePledgeDetails.cvv,
        employeePledgeDetails.zip,
        employeePledgeDetails.spouseName,
        employeePledgeDetails.spouseWorkPlace,
        employeePledgeDetails.spousePledgeAmount,
        employeePledgeDetails.payrollDeductionFrequency,
        employeePledgeDetails.organizationContactName,
        employeePledgeDetails.organizationContactEmail,
        employeePledgeDetails.submitDate,
        employeePledgeDetails.creditCardDeductionFrequencyId,
        employeePledgeDetails.installment,
        employeePledgeDetails.interval,
        employeePledgeDetails.isRecurringContribution,
        employeePledgeDetails.stepUpEnrollment,
        employeePledgeDetails.leadershipEnrollment,
        employeePledgeDetails.contributorId,
        employeePledgeDetails.stepUpMinDonationAmount,
        employeePledgeDetails.leadershipMinDonationAmount,
        employeePledgeDetails.organizationContactPhone,
        employeePledgeDetails.subscriptionId,
        employeePledgeDetails.campaignNotificationSentDate
    );
};

export const createEmployeePledgeViewModel = (dto: EmployeePledgeGetDto) => {
    if (dto) {
        return new EmployeePledgeViewModel(
            dto.id,
            dto.campaignId,
            dto.isWillingToContribute !== undefined && dto.isWillingToContribute !== null ? !dto.isWillingToContribute : null,
            dto.contributionOptionId,
            dto.contributionAmount,
            dto.paymentOptionId,
            dto.isWillingToStayAnonymous ? "1" : "2",
            dto.isWillingToBeIncludedInCompanyEvents ? "1" : "2",
            dto.isWillingToCombineSpouseContribution ? "1" : "2",
            dto.isWillingtoChooseAgenciesForContribution ? "1" : "2",
            dto.giftInvestments?.map(
                (x) => new EmployeePledgeGiftInvestmentViewModel(
                    x.id,
                    "",
                    x.giftInvestmentId,
                    x.pledgeId,
                    x.agencyId,
                    x.pledgeAmount,
                    x.otherAgencyName,
                    null
                )
            ),
            dto.contributionAmount,
            null,
            null,
            null,
            null,
            null,
            null,
            dto.spouseName,
            dto.spouseWorkPlace,
            dto.spousePledgeAmount,
            dto.payrollDeductionFrequency,
            dto.organizationContactName,
            dto.organizationContactEmail,
            dto.submitDate,
            dto.creditCardDeductionFrequencyId,
            dto.installment,
            dto.interval,
            dto.isRecurringContribution,
            dto.stepUpEnrollment,
            dto.leadershipEnrollment,
            dto.contributorId,
            dto.stepUpMinDonationAmount,
            dto.leadershipMinDonationAmount,
            dto.organizationContactPhone,
            dto.subscriptionId,
            dto.campaignNotificationSentDate
        );
    }
    return new EmployeePledgeViewModel();
};
